import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ListingStatus, SortDir, SortKey, UnitsQuery } from '$lib/api/types';

// URL-as-state for the units listing (decision: filter/sort/pagination encoded
// in URL, decision 8 of [DEV-94](/DEV/issues/DEV-94)).
//
// Single source of truth is the URL. Components read via useUnitsQueryState()
// and write via the returned `set` and `clear` helpers, which patch the
// URLSearchParams immutably.

const ARRAY_KEYS: ReadonlyArray<keyof UnitsQuery> = ['postnummer', 'status', 'source', 'propertyType', 'energyRating'];
const NUMBER_KEYS: ReadonlyArray<keyof UnitsQuery> = [
  'm2Min', 'm2Max', 'roomsMin', 'roomsMax', 'yearMin', 'yearMax', 'priceMin', 'priceMax',
  'page', 'pageSize'
];

const SORT_KEYS: SortKey[] = ['address', 'postnummer', 'city', 'm2', 'rooms', 'yearBuilt', 'rentKrPerMonth', 'krPerSqm', 'lastSeen'];
const SORT_DIRS: SortDir[] = ['asc', 'desc'];
const STATUSES: ListingStatus[] = ['active', 'pending', 'rented', 'delisted'];

function parseQuery(params: URLSearchParams): UnitsQuery {
  const out: UnitsQuery = {};
  const q = params.get('q');
  if (q) out.q = q;
  for (const k of ARRAY_KEYS) {
    const all = params.getAll(k);
    if (all.length === 0) continue;
    if (k === 'status') out.status = all.filter((x): x is ListingStatus => (STATUSES as string[]).includes(x));
    else (out as Record<string, unknown>)[k] = all;
  }
  for (const k of NUMBER_KEYS) {
    const v = params.get(k);
    if (v == null || v === '') continue;
    const n = Number(v);
    if (!Number.isFinite(n)) continue;
    (out as Record<string, number>)[k] = n;
  }
  const sort = params.get('sort');
  if (sort && (SORT_KEYS as string[]).includes(sort)) out.sort = sort as SortKey;
  const dir = params.get('dir');
  if (dir && (SORT_DIRS as string[]).includes(dir)) out.dir = dir as SortDir;
  return out;
}

function writeQuery(prev: URLSearchParams, patch: Partial<UnitsQuery>): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [key, value] of Object.entries(patch)) {
    next.delete(key);
    if (value == null) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      value.forEach((v) => next.append(key, String(v)));
    } else if (value !== '') {
      next.set(key, String(value));
    }
  }
  return next;
}

export interface UnitsQueryState {
  query: UnitsQuery;
  /** Partial update — undefined removes the key, [] removes a multi-value key. */
  set: (patch: Partial<UnitsQuery>) => void;
  clear: () => void;
  hasFilters: boolean;
  /** Count of active filter keys (excluding sort/page/pageSize). */
  filterCount: number;
}

const NON_FILTER_KEYS = new Set<string>(['sort', 'dir', 'page', 'pageSize']);

export function useUnitsQueryState(): UnitsQueryState {
  const [params, setParams] = useSearchParams();
  const query = useMemo(() => parseQuery(params), [params]);

  const set = useCallback(
    (patch: Partial<UnitsQuery>) => {
      setParams((prev) => writeQuery(prev, patch), { replace: false });
    },
    [setParams]
  );

  const clear = useCallback(() => {
    setParams(new URLSearchParams(), { replace: false });
  }, [setParams]);

  const filterCount = useMemo(() => {
    let n = 0;
    for (const key of params.keys()) {
      if (!NON_FILTER_KEYS.has(key)) n += 1;
    }
    return n;
  }, [params]);

  return { query, set, clear, hasFilters: filterCount > 0, filterCount };
}

export const __test = { parseQuery, writeQuery };
